/**
 * GMA-1617 (CompassCare Web) — app-shell layout.
 *
 * The dark command-center frame every facelift screen sits in: the left icon
 * Sidebar (rendered from the canonical WEB_NAV_SECTIONS) + a top bar (page
 * title + optional actions slot) + the routed content region (`children`).
 *
 * Presentational: `activeKey` and `onNavigate` are owned by the router; this
 * just lays out the chrome. a11y: a `banner` header, a `main` content landmark,
 * and the Sidebar's own labelled `nav`. Colors from the single design-token set.
 */
import React from "react";
import PropTypes from "prop-types";
import Sidebar from "./Sidebar";
import DemoDataBanner from "./DemoDataBanner";
import { WEB_NAV_SECTIONS } from "../../pages/web/webNavModel";
import { theme } from "../../styles/designTokens";
import { SessionCountdownSlot } from "../sessionCountdownSlot";

export default function WebShell({
  activeKey,
  onNavigate,
  title,
  actions,
  children,
}) {

  return (
    <div
      data-testid="web-shell"
      style={{
        display: "flex",
        height: "100vh",
        background: theme.bg,
        color: theme.textPrimary,
        fontFamily: theme.fontUI,
      }}
    >
      <Sidebar
        sections={WEB_NAV_SECTIONS}
        activeKey={activeKey}
        onNavigate={onNavigate}
      />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DemoDataBanner />
        <header
          data-testid="web-shell-topbar"
          role="banner"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            minHeight: 56,
            padding: "0 20px",
            borderBottom: `1px solid ${theme.border}`,
            background: theme.surface,
          }}
        >
          {title && String(title).trim() ? (
            // Only render the heading for a non-blank title — an empty (or
            // whitespace-only) <h1> is an accessibility problem (an empty
            // heading in the landmark).
            <h1 style={{ margin: 0, fontSize: 18, fontFamily: theme.fontUI }}>
              {title}
            </h1>
          ) : (
            <span aria-hidden="true" />
          )}
          {/* GMA-1909: reserved space, ahead of the actions slot. */}
          <SessionCountdownSlot />
          {actions !== null && actions !== undefined && typeof actions !== "boolean" ? (
            // Render the slot for genuinely-renderable nodes (0, "", elements)
            // but not for what React renders as nothing — null/undefined and
            // booleans (so the common `actions={cond && <X/>}` false case, and
            // `true`, don't produce an empty slot).
            <div
              data-testid="web-shell-actions"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              {actions}
            </div>
          ) : null}
        </header>
        <main
          data-testid="web-shell-content"
          style={{ flex: 1, minHeight: 0, overflow: "auto" }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

WebShell.propTypes = {
  /** Key of the active nav section (passed to the Sidebar). */
  activeKey: PropTypes.string,
  /** Called with the section when a nav item is chosen (router owns this). */
  onNavigate: PropTypes.func,
  /** Page title shown in the top bar. */
  title: PropTypes.string,
  /** Optional node rendered on the right of the top bar (e.g. quick actions). */
  actions: PropTypes.node,
  /** The routed page content. */
  children: PropTypes.node,
};
